<!-- Part of the SPARKL educational activity system, Copyright 2019 by Pepper Williams -->
<template><div class="k-main-collection k-courseindex2-wrapper">
	<div v-if="!lti_chooser" class="d-flex align-center mb-1">
		<k-icon color="primary" class="k-collection-header-icon mr-3">{{collection_icon}}</k-icon>
		<h1 class="k-page-title mb-2"><b v-html="page_title"></b></h1>
		<k-spacer/>
		<k-btn v-show="show_favorites_btn" color="secondary" class="k-tight-btn k-nocaps-btn" @click="go_to_favorites"><k-icon small class="mr-2">fas fa-chalkboard</k-icon>{{ favorites_button_text }}</k-btn>
	</div>

	<div v-if="initialized&&custom_tab_content_description&&!lti_chooser" class="k-custom-tab-content-description" :style="site_config.customized_tabs.classes.description_style" v-html="custom_tab_content_description"></div>

	<div v-show="initialized" class="d-flex align-center justify-center flex-wrap mb-4">
		<div class="k-courseindex2-control-wrapper">Show courses for:</div>
		<div class="k-courseindex2-control-wrapper"><k-select v-model="grade" :items="grades" label="Grade" accessible-label="Filter courses by grade" solo hide-details dense class="k-lp-grade-menu" :menu-props="{maxHeight:700}"></k-select></div>
		<div class="k-courseindex2-control-wrapper"><k-select v-model="subject" :items="subjects" label="Subject" accessible-label="Filter courses by subject" solo hide-details dense class="k-lp-subject-menu" :menu-props="{maxHeight:700}"></k-select></div>
		<div class="k-courseindex2-control-wrapper" style="min-width:200px"><k-text-field
			v-model="search_string"
			xprepend-inner-icon="fa fa-search" clearable clear-icon="fa fa-times-circle"
			aria-label="Search course titles"
			label="Search course titles…" single-line hide-details outlined dense background-color="#fff"
		></k-text-field></div>
		<k-btn v-if="!lti_chooser" outlined class="k-search-initialize-btn ml-2" @click="initiate_search">Search site content<div class="k-search-initialize-btn-icon-div"><k-icon small>fas fa-search</k-icon></div></k-btn>
		<!-- <k-spacer/> -->
	</div>

	<div v-show="initialized" 
		class="k-courseindex2-collections-container" 
		:style="collections_container_style"
	>
		<div v-if="collection_categories.length==0" class="text-center"><b style="color:#666"><i>No available courses match the grade, subject, and/or search terms you entered.</i></b></div>

		<div
			v-for="(category) in collection_categories"
			:key="category.title"
			class="k-courseindex2-category-wrapper"
			:style="category_wrapper_style(category)"
			:data-category-title="category.title"
			:aria-expanded="opened_category == category.title ? 'true' : 'false'"
			:aria-label="`${category.title} course collection category`"
			:aria-controls="category_group_id(category)"
			role="button"
			:tabindex="category.tabindex"
			@click.stop="category_clicked(category)"
			@mousedown="suppress_pointer_focus_open"
			@focus="category_focused(category)"
			@keydown="category_keydown($event, category)"
		>
			<k-hover v-slot:default="{hover}"><div class="k-courseindex2-category-container" :class="category_container_class(category, hover)" :style="category_container_style(category)">
				<div class="k-courseindex2-category-title k-courseindex2-shadow-text" :style="category_title_style(category)" v-html="category.title"></div>
				<div class="k-courseindex2-category-icon" :style="category_icon_style(category)"><img :src="subject_img(category.subject)" :alt="`${category.subject} subject image`"></div>
				<div
					class="k-courseindex2-subject-external-systems-btns"
					:style="subject_framework_btn_style(category)"
					:id="category_group_id(category)"
					role="group"
					:aria-label="top_row_group_label(category)"
				>
					<k-tooltip bottom>
						<template v-slot:activator="{on}">
							<div
								v-on="on"
								class="k-courseindex2-subject-es-btn k-courseindex2-subject-framework-btn"
								:data-category-title="category.title"
								data-top-row-item="true"
								:aria-label="`Open learning standards for ${category.title}`"
								role="button"
								tabindex="-1"
								@click.stop="show_framework(category)"
								@keydown="subject_button_keydown($event, category)"
							>
								<k-icon :class="opened_subcats[category.title]?'':'mr-2'" small color="#fff">
									{{ standards_icon }}
								</k-icon>
								
								{{standards_btn_label(category)}}
							</div>
						</template>
						
						{{ site_config.learning_standards_prefix }} Learning Standards for {{category.subject}}
					</k-tooltip>

					<k-tooltip bottom>
						<template v-slot:activator="{on}">
							<div
								v-on="on"
								v-if="show_extra_subject_links"
								class="k-courseindex2-subject-es-btn k-courseindex2-subject-community-btn"
								:data-category-title="category.title"
								data-top-row-item="true"
								:aria-label="`Open communities for ${category.title}`"
								role="button"
								tabindex="-1"
								@click.stop="show_community(category)"
								@keydown="subject_button_keydown($event, category)"
							>
								<k-icon :class="opened_subcats[category.title]?'':'mr-2'" small color="#fff">
									fas fa-message
								</k-icon>
								
								{{community_btn_label(category)}}
							</div>
						</template>
						
						{{ site_config.communities_prefix }} Communities for {{category.subject}}
					</k-tooltip>

					<k-tooltip bottom>
						<template v-slot:activator="{on}">
							<div
								v-on="on"
								v-if="show_extra_subject_links"
								class="k-courseindex2-subject-es-btn k-courseindex2-subject-pd-btn"
								:data-category-title="category.title"
								data-top-row-item="true"
								:aria-label="`Open professional development hub for ${category.title}`"
								role="button"
								tabindex="-1"
								@click.stop="show_pd(category)"
								@keydown="subject_button_keydown($event, category)"
							>
								<k-icon :class="opened_subcats[category.title]?'':'mr-2'" small color="#fff">
									fas fa-graduation-cap
								</k-icon>
								
								{{pd_btn_label(category)}}
							</div>
						</template>
						
						{{ site_config.pd_hub_prefix }} - PD Hub
					</k-tooltip>

					<k-tooltip bottom>
						<template v-slot:activator="{on}">
							<div
								v-if="show_pl_event_icon[category.subject]"
								v-on="on"
								class="k-courseindex2-subject-es-btn k-courseindex2-subject-pd-btn"
								:data-category-title="category.title"
								data-top-row-item="true"
								:aria-label="`Open PL Events link for ${category.title}`"
								role="button"
								tabindex="-1"
								@click.stop="open_pl_event_link(category)"
								@keydown="subject_button_keydown($event, category)"
							>
								<k-icon :class="opened_subcats[category.title]?'':'mr-2'" small color="#fff">
									far fa-calendar
								</k-icon>

								{{ opened_subcats[category.title] ? '' : 'PL Events'}}
							</div>
						</template>
						
						{{ category.title }} - PL Events
					</k-tooltip>
				</div>

				<div
					class="k-courseindex2-subcats-container"
					:style="subcats_container_style(category)"
					v-show="opened_category==category.title"
					role="group"
					:aria-label="school_levels_group_label(category)"
				>
					<div
						v-for="(subcat, subcat_index) in category.subcats"
						:key="subcat.title"
						class="k-courseindex2-subcat"
						:class="subcat_button_class(category, subcat)"
						:style="subcat_style(category, subcat, subcat_index)"
						:data-category-title="category.title"
						:data-subcat-title="subcat.title"
						data-top-row-item="true"
						:aria-label="subcat_accessible_label(category, subcat)"
						:aria-pressed="opened_subcats[category.title] == subcat.title ? 'true' : 'false'"
						role="button"
						tabindex="-1"
						@click.stop="subcat_clicked(category, subcat)"
						@mousedown="suppress_pointer_focus_open"
						@focus="subcat_focused(category, subcat)"
						@keydown="subcat_keydown($event, category, subcat)"
					>
						<div v-html="subcat.title"></div>
					</div>
				</div>
				<div
					v-for="(subcat) in category.subcats"
					:key="subcat.title"
					class="k-courseindex2-subcat-collections-container"
					:data-category-title="category.title"
					:data-subcat-title="subcat.title"
					v-show="opened_subcats[category.title]==subcat.title"
					:class="subcat_collections_container_class(category, subcat)"
					:style="subcat_collections_container_style(category, subcat)"
					role="group"
					:aria-label="collections_group_label(category, subcat)"
				>
					<div
						v-for="(collection, collection_index) in subcat.collections"
						:key="collection.course_code"
						class="k-courseindex2-collection elevation-3"
						:class="collection_class(category, subcat, collection)"
						:style="collection_style(category, subcat, collection)"
						:data-category-title="category.title"
						:data-subcat-title="subcat.title"
						:data-collection-index="collection_index"
						:aria-label="collection_accessible_label(category, subcat, collection)"
						role="button"
						tabindex="-1"
						@click.stop="collection_clicked(category, subcat, collection)"
						@keydown="collection_keydown($event, category, subcat, collection_index)"
					>
						<div class="k-courseindex2-bold-text" :style="[{'color': U.get_contrast_color(collection.color, 'dark')}, U.get_contrast_color(collection.color, 'dark') === '#FFFFFF' ? {} : {'font-weight':'bold !important'}]" v-html="lp_title(collection.title)"></div>
						<div class="k-courseindex2-collection-icons">
							<k-icon color="#000" small class="mr-0">fas fa-person-chalkboard</k-icon>
							<!-- <k-icon color="#000" small>fas fa-long-arrow-right</k-icon> -->
						</div>
						<div :style="{'color': U.get_contrast_color(collection.color, 'dark')}" class="k-courseindex2-collection-code">{{collection.course_code}}</div>
					</div>
				</div>
			</div></k-hover>
		</div>
	</div>
	<div v-if="user_can_access_inactive_collections" class="mt-4 d-flex">
		<k-spacer/>
		<k-checkbox class="mt-0 pt-0" label="Show inactive collections" v-model="show_inactive_collections" hide-details off-icon="far fa-square" on-icon="fas fa-check-square"></k-checkbox>
		<k-spacer/>
	</div>
</div></template>

<script>
import { mapState, mapGetters } from 'vuex'

export default {
	props: {
		lti_chooser: { type: Boolean, required: false, default() { return false }},
	},

	data() { return {
		initialized: false,
		grade: '— All Grades —',
		subject: '— All Subjects —',
		search_string:'',
		search_terms: [],
		collection_categories: [],
		debug_uuid: U.new_uuid(),
		// the following are set in reorganize_categories
		tile_width:200,
		grid_margin: 25,
		num_columns: 3,	
		collections_container_style: '',
		subject_external_systems_extra_height: 0,
		set_collection_categories_debounced: null,
		user_can_access_inactive_collections: false,
		pointer_focus_suppressed: false,
	}},

	computed: {
		...mapState(['user_info', 'all_courses_loaded', 'all_courses', 'course_update_trigger', 'grades', 'site_config']),

		...mapGetters(['signed_in', 'child_count', 'child_data_showing', 'role']),

		page_title() { return this.site_config.customized_tabs?.classes?.title || 'Course Content Collections' },
		custom_tab_content_description() { return this.site_config.customized_tabs?.classes?.description },
		collection_icon() { 
			// use this construct to get the right icon, which might be customized by site_config
			return new Learning_Progression({collection_type:'course'}).collection_icon()
		},

		child_email_showing: {
			get() { return this.$store.state.lst.child_email_showing },
			set(val) { this.$store.commit('lst_set', ['child_email_showing', val]) }
		},

		opened_category: {
			get() { return this.$store.state.lst.courseindex_opened_category },
			set(val) { this.$store.commit('lst_set', ['courseindex_opened_category', val]) }
		},

		opened_subcats: {
			// opened_subcats[category.title] = null or subcat.title
			get() { return this.$store.state.lst.courseindex_opened_subcats },
			set(val) { this.$store.commit('lst_set', ['courseindex_opened_subcats', val]) }
		},

		show_inactive_collections: {
			get() { return this.$store.state.lst.show_inactive_collections },
			set(val) { this.$store.commit('lst_set', ['show_inactive_collections', val]) }
		},

		show_favorites_btn() {
			if (!this.signed_in) return false
			if (this.site_config.use_vx_nav) return false
			// for parents, we have to know which child they're viewing to be able to go to favorites
			if (this.role == 'parent' && empty(this.child_data_showing?.sis_student_classes)) return false
			return true
		},

		favorites_button_text() {
			if (this.user_info.role === 'parent') return "My Child’s Courses"
			// if (true) return 'My Courses'
			return 'Favorite Collections'
		},

		grades() {
			let arr = ['— All Grades —']
			for (let grade of this.$store.state.grades) {
				let text
				if (grade == 'K') text = 'Kindergarten'
				else if (grade == 'PreK' || grade == 'PK') text = 'Pre-Kindergarten'
				else if (!isNaN(grade*1)) text = 'Grade ' + grade
				else text = grade

				arr.push({ value: grade, text: text})
			}
			return arr
		},

		subjects() {
			let arr = ['— All Subjects —']
			for (let subject in this.$store.state.subjects) {
				arr.push({ value: subject, text: subject})
			}
			return arr
		},

		// see watcher below
		component_showing() {
			// if (this.lti_chooser) return true

			// in the vx navigation scheme, we check the route name like this
			if (this.site_config.use_vx_nav) {
				return this.$route.name.indexOf('courses') == 0

			// in the old navigation scheme it's more complicated
			} else {
				if (this.$store.state.lst.welcome_section_showing == 'classes' && this.$store.state.lp_showing == 0) {
					if (!this.signed_in) return true
					else return (this.$store.state.lst.course_index_view_flavor == 'lpindex')
				}
				return false
			}
			// return this.$store.state.lst.welcome_section_showing == 'classes' && this.$store.state.lst.course_index_view_flavor == 'lpindex' && this.$store.state.lp_showing == 0
		},

		// note that tile_width, grid_margin, and num_columns are set in reorganize_categories
		tile_height() { return this.tile_width },

		tile_width_open() { return this.tile_width * 3 + (2 * this.grid_margin) },

		tile_padding() { return Math.round(this.tile_width / 12.5) },

		subcat_width() { return Math.round(this.tile_width * 0.45) },

		cat_icon_large_width() { return this.tile_width / 2.5 },

		cat_icon_small_width() { return this.tile_width / 5 },

		collection_width() { return Math.round(this.tile_width / 1.67) },

		show_extra_subject_links() {
			if (!this.signed_in) return false
			return this.site_config.show_extra_subject_links_in_course_index == 'yes'
		},

		show_pl_event_icon() {
			let output = {}
			if (this.signed_in && this.site_config.pl_event_links.enabled) {
				this.collection_categories.forEach(x => {
					output[x.subject] = !!this.site_config.pl_event_links[x.subject]
				})
			}
			return output
		},

		standards_icon() {
			return this.site_config.customized_tabs.standards?.icon || 'fas fa-map'
		},
	},

	watch: {
		// when the screen is resized or a course gets updated, *note* that we need to update collection categories...
		'$vuetify.breakpoint.width'() { 
			if (this.component_showing || $(this.$el).is(':visible')) this.set_collection_categories()
		},

		'window.location': { deep:true, immediate:true, handler() {
			// this makes things so that if the user opens to, e.g., `/courses/Science/High School`, we will immediately open to that category and subcategory
			let o = window.location.pathname.match(/\/courses\/([^\/]+)(\/(.*))?/)
			if (o) {
				this.opened_category = this.decode_path_section(decodeURI(o[1]))
				let subcat = (o[3]) ? this.decode_path_section(decodeURI(o[3])) : null
				let val = extobj(this.opened_subcats)
				val[this.opened_category] = subcat
				this.opened_subcats = val
				this.reorganize_categories()
			}
		}},

		show_inactive_collections() { 
			this.set_collection_categories()
		},

		course_update_trigger() { 
			this.set_collection_categories()
		},

		// then when the component is showing, call set_collection_categories to make sure everything fits right
		component_showing: {
			handler() { 
				if (this.component_showing) {
					this.set_collection_categories()
					this.set_location_path()
				}
			}, deep:true, immediate:true
		},

		search_string() {
			if (empty(this.search_string)) {
				this.search_terms = []
			} else {
				// convert string to lower case and split on spaces
				this.search_terms = $.trim(this.search_string).toLowerCase().split(/\s+/)
			}
			this.set_collection_categories()
		},

		grade() { this.set_collection_categories() },

		subject() { this.set_collection_categories() },
	},

	created() {
		vapp.courseindex2 = this
		if (!this.all_courses_loaded) {
			this.$store.dispatch('get_all_courses', 'initial').then(() => {
				this.$nextTick(() => {
					this.initialize()
				})
			}).catch(() => {
				console.error('error in get_all_courses')
				this.go_to_favorites()
			})
		} else {
			this.initialize()
		}
	},

	// watch the external-systems-btns area so the subject tile can resize when the buttons wrap/unwrap
	mounted() {
		this.subject_external_systems_resize_observer = new ResizeObserver(() => {
			if (this.measure_subject_external_systems_height()) {
				this.reorganize_categories()
			}
		})
	},

	beforeDestroy() {
		if (this.subject_external_systems_resize_observer) {
			this.subject_external_systems_resize_observer.disconnect()
		}
	},
	
	methods: {
		initialize() {
			// this will run after all courses are loaded
			setTimeout(() => {
				this.set_collection_categories()
				this.initialized = true
			})
		},

		external_search(search_string) {
			this.search_string = search_string
		},

		go_to_favorites() {
			window.history.replaceState(null, '', '/courses')
			if (!this.signed_in) this.$store.commit('lst_set', ['unsigned_index_view_flavor', 'favorites'])
			else this.$store.commit('lst_set', ['course_index_view_flavor', 'favorites'])
		},

		set_collection_categories() {
			// Using this debounce seems to prevent us from running this multiple times
			if (empty(this.set_collection_categories_debounced)) {
				this.set_collection_categories_debounced = U.debounce(function() {
					if (empty(this.grade) && empty(this.subject) && this.search_terms.length == 0) return []

					// filter all courses by grade/subject/search_terms, as well as other criteria
					// side-effect: set user_can_access_inactive_collections
					this.user_can_access_inactive_collections = false
					let arr = this.all_courses.filter(lp => {
						// for inactive lp's, only show to people explicitly authorized as viewers (this includes admins)
						if (lp.active == 'no') {
							if (!lp.user_can_view_lp()) return false
							this.user_can_access_inactive_collections = true

							// and if show_inactive_collections is false, hide even if they're allowed to see it
							if (!this.show_inactive_collections) return false
						}

						// if we have site_config.grades_to_include and/or site_config.subjects_to_include, limit to them
						if (this.site_config.grades_to_include || this.site_config.subjects_to_include) {
							// but *DO* include LP's updated after include_lps_updated_after (if set), even if they don't match the grades/subjects to include
							if (empty(this.site_config.include_lps_updated_after) || lp.updated_at < this.site_config.include_lps_updated_after) {
								if (this.site_config.subjects_to_include) {
									if (!this.site_config.subjects_to_include.includes(lp.subject)) return false
								}

								if (this.site_config.grades_to_include) {
									let grade_included = false
									for (let grade of this.site_config.grades_to_include) {
										if (U.grades_include(lp, grade)) {
											grade_included = true
											break
										}
									}
									if (!grade_included) return false
								}
							}
						}

						// only include collection_type 'course' here
						if (lp.collection_type != 'course') return false

						// when we first create a new LP to be entered, its title will be false
						if (lp.title == '') return false

						// if all search_terms appear in the title, return true
						let tlc = lp.title.toLowerCase()
						for (let term of this.search_terms) {
							if (tlc.indexOf(term) == -1) return false
						}

						if (!empty(this.grade) && this.grade != this.grades[0] && !U.grades_include(lp, this.grade)) return false
						if (!empty(this.subject) && this.subject != this.subjects[0] && lp.subject != this.subject) return false

						return true
					})

					// sort courses
					arr.sort((a,b)=>{
						// put active courses before inactive
						if (a.active != 'no' && b.active == 'no') return -1 
						if (a.active == 'no' && b.active != 'no') return 1 

						// Single-grade courses come first, in grade order. Multi-grade
						// courses (e.g. grades 9–12) follow in alphabetical title order.
						const a_single_grade = a.grade_low == a.grade_high
						const b_single_grade = b.grade_low == b.grade_high
						if (a_single_grade != b_single_grade) return a_single_grade ? -1 : 1
						if (a_single_grade && b_single_grade) {
							let gd = U.grade_value(a.grade_low) - U.grade_value(b.grade_low)
							if (gd != 0) return gd
						}

						// then by subject (not relevent here, since we categorize by subject)
						if (a.subject < b.subject) return -1
						if (b.subject < a.subject) return 1

						// then by title, using natural sort algorithm
						return U.natural_sort(a.title, b.title)
					})

					// create preliminary categories and subcats
					let low_grade = this.$store.state.grades[0]
					let high_grade = this.$store.state.grades[this.$store.state.grades.length-1]
					let carr = []
					let order = 0
					for (let subject in this.$store.state.subjects) {
						let cat = {
							original_order: order,
							color: this.$store.state.subjects[subject].color,
							title: subject,
							subject: subject,
							framework_identifier: this.$store.state.subjects[subject].framework_identifier,
							tabindex: 0,
							// for now hard-code subcats
							subcats: [
								{ title: 'Elementary School', grade_low: low_grade, grade_high: '5', collections: []},
								{ title: 'Middle School', grade_low: '6', grade_high: '8', collections: []},
								{ title: 'High School', grade_low: '9', grade_high: '12', collections: []},
								{ title: 'Adult', grade_low: 'Adult', grade_high: high_grade, collections: []},
							],
						}

						carr.push(cat)
						++order
					}
					// include an "Other Courses" category, unless we're limiting by subject
					if (empty(this.site_config.subjects_to_include)) carr.push({
						original_order: order,
						color: '33,33,33',
						title: 'Other Courses',
						subject: 'other',
						framework_identifier: null,
						subcats: [
							{ title: 'Elementary School', grade_low: low_grade, grade_high: '5', collections: []},
							{ title: 'Middle School', grade_low: '6', grade_high: '8', collections: []},
							{ title: 'High School', grade_low: '9', grade_high: '12', collections: []},
							{ title: 'Adult', grade_low: 'Adult', grade_high: high_grade, collections: []},
						],
					})

					// add filtered courses to categories/subcats
					for (let collection of arr) {
						let cat = carr.find(x=>x.subject == collection.subject)
						if (!cat) cat = carr[carr.length-1]	// "Other Courses"
						// find subcat whose grades include the collection's grades
						// TODO: deal with CTAE/Arts where subcats aren't grade-based
						let subcat = cat.subcats.find(x=>{
							return U.grades_include(x, collection.grade_low, collection.grade_high, 'fully_include')
						})

						// If the collection fits in to a single grade band, push it and exit loop...
						if (subcat) {
							subcat.collections.push(collection)
							continue
						}

						// ...otherwise, add collection to all appropriate grade bands -- unless we're limiting to certain grades
						if (!this.site_config.grades_to_include) {
							cat.subcats.forEach(x => {
								if (U.grades_include(x, collection.grade_low, collection.grade_high)) {
									x.collections.push(collection)
								}
							})
						}
					}

					// remove subcats and cats that have no courses, and fill in additional info for each cat
					let fcarr = []
					let opened_subcats = {}
					for (let cat of carr) {
						// create opened_subcats val if necessary; inherit from existing value if there
						opened_subcats[cat.title] = this.opened_subcats[cat.title] ?? ''

						// ...otherwise, add collection to all appropriate grade bands -- but not if we're limiting to certain grades and don't have any search terms
						// if (this.grade != '— All Grades —' || this.subject != '— All Subjects —' || this.search_terms.length > 0 || !this.site_config.grades_to_include) {

						// remove subcats with no courses, unless we're limiting grades to include, in which case show all subcats for demo purposes. BUT: if user has chosen a grade or entered search terms, do remove subcats
						if (this.grade != '— All Grades —' || this.subject != '— All Subjects —' || this.search_terms.length > 0 || !this.site_config.grades_to_include) {
							for (let i = cat.subcats.length-1; i >= 0; --i) {
								if (cat.subcats[i].collections.length == 0) {
									cat.subcats.splice(i, 1)
								}
							}
						}

						// if at least one subcat is left over, include the collection
						if (cat.subcats.length > 0) {
							// TODO: inherit some things from previous incarnation
							cat.top = ''
							cat.left = ''
							cat.column = 0
							cat.row = 0

							fcarr.push(cat)

							// if exactly one subcat is left, open it
							if (cat.subcats.length == 1) opened_subcats[cat.title] = cat.subcats[0].title
						}
					}

					this.collection_categories = fcarr
					this.opened_subcats = opened_subcats

					// if only one category is left here, open it
					if (fcarr.length == 1) {
						this.opened_category = fcarr[0].title
					}

					// whenever we change categories, have to immediately reorganize categories
					this.reorganize_categories()
				}, 10)
			}
			// call the debounce fn
			this.set_collection_categories_debounced()
		},

		subject_img(subject) { 
			// thumbnail images to show for each subject. these default images live in the /src/logos/subject-images directory that is checked into git
			// to override for a cureum instance:
			//   - add a /src/logos/alt_subject_img directory in the instance and put the images there
			//   - specify the image file paths in the config/config-admin.php file like so:
			//     'English Language Arts' => ['framework_identifier' => 'd5af742b-1042-4647-a45a-027e4c4a2f1f', 'img' => '/src/logos/alt_subject_img/ela.jpg', 'color' => '#01695C'],

			let default_subject_imgs = {
				'Language Arts': '/src/logos/subject-images/ela.jpg',
				'English Language Arts': '/src/logos/subject-images/ela.jpg',
				'Mathematics': '/src/logos/subject-images/math.jpg',
				'Science': '/src/logos/subject-images/science.jpg',
				'Social Studies': '/src/logos/subject-images/social_studies.jpg',
				'Physical Education': '/src/logos/subject-images/pe.jpg',
				'Health': '/src/logos/subject-images/health.jpg',
				'Computer Science': '/src/logos/subject-images/compsci.jpg',
				'World Languages': '/src/logos/subject-images/languages.jpg',
				'English Language Development': '/src/logos/subject-images/misc.jpg',
				'Fine Arts: Dance': '/src/logos/subject-images/dance.jpg',
				'Fine Arts: Dramatic Arts/Theatre': '/src/logos/subject-images/theatre.jpg',
				'Fine Arts: Music': '/src/logos/subject-images/music.jpg',
				'Fine Arts: Visual Art': '/src/logos/subject-images/visual_arts.jpg',
				'CTAE': '/src/logos/subject-images/ctae.jpg',
				'Training Courses': '/src/logos/subject-images/training.jpg',
				'misc': '/src/logos/subject-images/misc.jpg',
			}

			// if we have an image specified in config-admin, use it
			if (this.$store.state.subjects[subject]?.img) {
				return this.$store.state.subjects[subject].img

			// otherwise use these defaults
			} else {
				let img = default_subject_imgs[subject]
				if (img) return img
				return default_subject_imgs.misc
			}
		},

		category_wrapper_style(category) {
			let s = ''
			s += sr('top:$1;', category.top)
			s += sr('left:$1;', category.left)
			return s
		},

		category_container_class(category, hover) {
			let s = ''
			if (this.opened_category == category.title) {
				s += ' k-courseindex2-category-open'
				if (this.opened_subcats[category.title]) s += ' k-courseindex2-category-subcategory-selected'
			}
			if (hover) s += ' k-courseindex2-category-hovered'
			return s
		},

		category_container_style(category) {
			let s = ''
			let w, h
			if (this.opened_category == category.title) {
				w = this.tile_width_open
				let rows = this.get_protruding_down_val(category) + 1
				h = this.tile_height * rows + ((rows - 1) * this.grid_margin)
				h += this.subject_external_systems_extra_height
			} else {
				w = this.tile_width
				h = this.tile_height
			}

			let br = Math.round(this.tile_width / 8)

			s += sr('width:$1;height:$2;padding:$3;border-radius:$4; background-color:$5', w+'px', h+'px', this.tile_padding+'px', br+'px', category.color)
			return s
		},

		category_icon_style(category) {
			let s = ''
			let l, t, w
			if (this.opened_category != category.title) {
				w = this.cat_icon_large_width
				l = sr('calc(100% - $1)', (w + this.tile_padding - 4)+'px')
				t = l
			} else {
				w = this.cat_icon_small_width
				l = (this.tile_padding - 4) + 'px'
				t = (this.tile_padding + 2) + 'px'
			}
			let br = Math.round(w / 6.67)
			s += sr('width:$1;height:$1;left:$2;top:$3;border-radius:$4;', w+'px', l, t, br+'px')

			return s
		},

		category_title_style(category) {
			let s = ''
			let fs = this.tile_width / 9
			s += sr('font-size:$1;', fs+'px')
			if (this.opened_category == category.title) {
				let pl = this.cat_icon_small_width + (this.tile_padding / 2 - 4)
				let w = this.tile_width_open - (category.subcats.length * this.subcat_width + (category.subcats.length + 1) * Math.round(this.grid_margin / 3)) - this.tile_padding*2
				s += sr('width:$1;padding-left:$2;', w+'px', pl+'px')
			}
			return s
		},

		subcats_container_style(category) {
			let s = ''
			let br = Math.round(this.tile_width / 12.5)
			s += sr('right:$1;top:$2;border-radius:$3', (this.tile_padding-4)+'px', (this.tile_padding-4)+'px', br+'px')
			return s
		},

		subcat_button_class(category, subcat) {
			let s = ''
			// s += U.subject_tile_css(category.subject) + '-medlight'	// this was taken out in nc-cureum
			if (this.opened_category == category.title && this.opened_subcats[category.title] == subcat.title) {
				s += ' k-courseindex2-subcat-selected'
			}
			return s
		},

		subcat_style(category, subcat, subcat_index) {
			let s = ''
			let w = this.subcat_width
			let h = this.subcat_width
			let p = Math.round(this.tile_width / 25)
			let m = Math.round(this.grid_margin / 3)
			let ml = (subcat_index == 0) ? m : m / 2
			let mr = (subcat_index == category.subcats.length-1) ? m : m / 2
			let fs = w / 6.67
			let br = Math.round(this.tile_width / 20)
			s += sr('width:$1;height:$2;margin:$3 $4 $3 $5;font-size:$6;border-radius:$7;padding:$8;', w+'px', h+'px', m+'px', mr+'px', ml+'px', fs+'px', br+'px', p+'px')
			return s
		},

		subcat_collections_container_class(category, subcat) {
			let s = ''
			if (this.opened_category == category.title && this.opened_subcats[category.title] == subcat.title) {
				s += ' k-courseindex2-subcat-collections-container-selected'
			}
			return s
		},

		subcat_collections_container_style(category, subcat) {
			let s = ''
			let t = Math.round(this.tile_width * 0.74)
			s += sr('top:$1;', t+'px')
			return s
		},

		subject_framework_btn_style(category) {
			let s = ''
			if (this.opened_category == category.title) {
				let t
				if (!this.opened_subcats[category.title]) {
					t = this.tile_height * 0.72
				} else {
					t = Math.round(this.tile_width * 0.74) - 40
				}
				s += sr('top:$1;', t+'px')
			}
			return s
		},

		// Measure extra height when external-systems-btns wrap onto additional rows
		// Return true only when the height changes so the category grid can be recalculated
		measure_subject_external_systems_height() {
			let extra_height = 0

			if (this.opened_category) {
				const category = this.collection_categories.find(x => x.title == this.opened_category)

				if (category) {
					const group = this.$el.querySelector(`#${this.category_group_id(category)}`)
					const buttons = group ? Array.from(group.querySelectorAll('.k-courseindex2-subject-es-btn')) : []

					if (group && buttons.length > 0) {
						const single_row_height = Math.max(...buttons.map(x => x.offsetHeight))
						extra_height = Math.max(0, group.offsetHeight - single_row_height)
					}
				}
			}

			if (extra_height == this.subject_external_systems_extra_height) return false

			this.subject_external_systems_extra_height = extra_height
			return true
		},

		observe_subject_external_systems() {
			if (!this.subject_external_systems_resize_observer) return

			let group = null

			if (this.opened_category) {
				const category = this.collection_categories.find(x => x.title == this.opened_category)
				if (category) {
					group = this.$el.querySelector(`#${this.category_group_id(category)}`)
				}
			}

			if (group == this.subject_external_systems_observed_element) return

			if (this.subject_external_systems_observed_element) {
				this.subject_external_systems_resize_observer.unobserve(this.subject_external_systems_observed_element)
			}

			this.subject_external_systems_observed_element = group

			if (group) {
				this.subject_external_systems_resize_observer.observe(group)
			} else if (this.measure_subject_external_systems_height()) {
				this.reorganize_categories()
			}
		},

		standards_btn_label(category) {
			if (this.opened_subcats[category.title]) return '' //  'ALCOS'
			else return 'Learning Standards'
		},

		community_btn_label(category) {
			if (this.opened_subcats[category.title]) return '' //  'ALCOS'
			else return 'Communities'
		},

		pd_btn_label(category) {
			if (this.opened_subcats[category.title]) return '' //  'ALCOS'
			else return this.site_config.pd_hub_prefix
		},

		safe_id_fragment(val) {
			return String(val || '')
				.toLowerCase()
				.replace(/[^a-z0-9]+/g, '-')
				.replace(/^-+|-+$/g, '')
		},

		category_group_id(category) {
			return `course-category-group-${this.safe_id_fragment(category.title)}`
		},

		top_row_group_label(category) {
			return `Top actions for ${category.title}`
		},

		school_levels_group_label(category) {
			return `School levels for ${category.title}`
		},

		subcat_accessible_label(category, subcat) {
			return `${subcat.title} in ${category.title}`
		},

		collections_group_label(category, subcat) {
			return `Collections in ${subcat.title} for ${category.title}`
		},

		collection_accessible_label(category, subcat, collection) {
			return `Open ${collection.title} in ${subcat.title} for ${category.title}`
		},

		lp_title(s) {
			// add <wbr> tags after slashes
			s = s.replace(/\//g, '/<wbr>')
			return s
		},

		collection_class(category, subcat, collection) {
			let s = ''
			// if (empty(collection.color)) s += ` k-framework-color-${category.color}-darkest`
			// if (collection.color.length < 3) s += ` k-list-color-${collection.color}-darkest`
			// s += ` k-framework-color-${category.color}-border-light`

			if (collection.active == 'no') {
				s += ' k-lp-inactive'
			}

			if (!collection.cmap_specified) {
				s += ' k-lp-cmap-unspecified'
			}

			// PW 6/15/2026: U.longest_word_length(lp_title(collection.title)) was throwing a bug for some users in some situations; I fixed this by changing to collection.title 
			// (I also made a safety tweak to the longest_word_length utility fn just in case)
			if (!empty(collection.title)) {
				if (collection.title.length > 50 || U.longest_word_length(collection.title) > 18) {
					s += ' k-lp-tile-extra-long-title'
				} else if (collection.title.length > 30 || U.longest_word_length(collection.title) > 12) {
					s += ' k-lp-tile-long-title'
				}
			}

			if (this.user_info.role == 'admin') {
				s += ' k-courseindex2-collection-code-visible'
			}

			return s
		},

		collection_style(category, subcat, collection) {
			let w = this.collection_width
			let h = this.collection_width
			let m = Math.round(this.grid_margin / 6)
			let br = Math.round(this.tile_width / 20)
			let fs = w / 8
			let p = Math.round(this.tile_width / 25)
			const style = {'width':`${w}px`, 'height':`${h}px`, 'margin':`${m}px`, 'font-size':`${fs}px`,'border-radius':`${br}px`, 'padding':`${p}px`}
			const background_color = U.collection_color_style(collection, 'med_dark')
			return ({
				...style,
				...background_color
			})
		},

		reorganize_categories() {
			// if (this.$vuetify.breakpoint.width < 0) return 100	// force an update whenever screen is resized

			// whenever this is called, put the categories in their original order
			this.collection_categories.sort((a,b)=>a.original_order-b.original_order)

			// we set the "tabindex" property of each category to the original category order; 
			// this way if we have to shift the order below, they will still tab properly
			for (let i = 0; i < this.collection_categories.length; ++i) {
				this.collection_categories[i].tabindex = i+1
			}
			
			let container_width = $(this.$el).width()

			let max_tw = 200
			let min_tw = 160

			let tw = max_tw
			if ((container_width / 3) < (max_tw * 1.25)) {
				tw = Math.round((container_width / 3) / 1.25)
				if (tw < min_tw) tw = min_tw
			}

			this.num_columns = Math.floor(container_width / tw)
			this.grid_margin = Math.round((container_width - this.num_columns * tw) / (this.num_columns + 1))

			// if this would give us fewer than 4 columns, see if we can squeeze in 4
			if (this.num_columns < 4 && tw > min_tw && ((min_tw*1.1) * 4) < container_width) {
				tw = Math.round((container_width / 4) / 1.1)
				if (tw < min_tw) tw = min_tw
				this.num_columns = Math.floor(container_width / tw)
				this.grid_margin = Math.round((container_width - this.num_columns * tw) / (this.num_columns + 1))
			}


			if (this.grid_margin < (tw / 10)) {
				// this.num_columns -= 1
				// this.grid_margin = Math.round((container_width - this.num_columns * tw) / (this.num_columns + 1))
				tw = Math.round(tw * 0.9)
				this.grid_margin = Math.round((container_width - this.num_columns * tw) / (this.num_columns + 1))
			}

			this.tile_width = tw

			let column = 0, row = 0, opened_cat, opened_column_start, opened_row_start, shift_opened_cat, blocked_cells
            for (let category of this.collection_categories) {
				category.column = column
				category.row = row

				// if we find the opened category, set opend_column_start and opened_row_start to the starting position of the opened category
				if (this.opened_category == category.title) {
					opened_cat = category
					opened_column_start = column
					opened_row_start = row
					// find out if if the opened category is going to have to move left; if so, adjust opened_column_start accordingly
					shift_opened_cat = Math.max(0, (column + 3) - this.num_columns)
					if (shift_opened_cat > 0) opened_column_start -= shift_opened_cat
				}

				++column; if (column >= this.num_columns) { column = 0; ++row; }

				// set initial positions, in case we don't have an opened category
				this.set_position(category, category.column, category.row)
			}

			if (opened_cat) {
				// if we have to shift the opened category left, use splices to shift it back in the list, so we make sure to encounter it at the right point in the grid
				// if we *didn't* do this, some categories would sometimes be hidden by opened categories
				if (shift_opened_cat > 0) {
					let index = this.collection_categories.findIndex(x=>x==opened_cat)
					this.collection_categories.splice(index, 1)
					this.collection_categories.splice(index - shift_opened_cat, 0, opened_cat)
				}

				// now position all the categories, taking the opened category's cells into account
				let protruding_down = this.get_protruding_down_val(opened_cat)

				column = 0
				row = 0
				for (let category of this.collection_categories) {
					// when we get to the opened category, set its position based on opened_column_start and opened_row_start
					if (category == opened_cat) {
						category.column = opened_column_start
						category.row = opened_row_start
						this.set_position(category, opened_column_start, opened_row_start)
						column += 3; if (column >= this.num_columns) { column = 0; ++row; }

						// set blocked_cells
						blocked_cells = []
						for (let i = opened_column_start; i < (opened_column_start+3); ++i) {
							blocked_cells[i] = []
							for (let j = opened_row_start; j < (opened_row_start + protruding_down + 1); ++j) {
								blocked_cells[i][j] = true
							}
						}
						continue
					}

					// once we've positioned the opened category, make sure we don't position subsequent categories in blocked_cells
					if (blocked_cells && blocked_cells[column] && blocked_cells[column][row]) {
						// increment column/rows until blocked_cells are cleared
						while (blocked_cells[column] && blocked_cells[column][row]) {
							++column; if (column >= this.num_columns) { column = 0; ++row; }
						}
					}

					// set category column/row and position
					category.column = column
					category.row = row
					this.set_position(category, column, row)
					++column; if (column >= this.num_columns) { column = 0; ++row; }
				}

				// if the category is protruding down...
				if (protruding_down > 0) {
					// then it may be going below the current row value; if so...
					if (row < blocked_cells[blocked_cells.length-1].length) {
						// set row to the bottom row of blocked_cells
						row = blocked_cells[blocked_cells.length-1].length
						// and if column isn't 0, adjust row down so that when we bump it up below it'll be correct
						if (column != 0) row -= 1
					}
				}
			}

			// set height to accommodate the bottom row; row is 0-indexed, so unless column is 0, adjust it up for the calculation below
			if (column != 0) row += 1
			this.collections_container_style = sr('height:$1;', (row * this.tile_width + (row+1) * this.grid_margin + this.subject_external_systems_extra_height)+'px')

			this.$nextTick(() => {
				this.observe_subject_external_systems()
			})
		},

		get_protruding_down_val(category) {
			// this returns the number of rows in the grid the given category "protrudes" down
			// if none of the category's subcategories are open, the value is 0
			if (!this.opened_subcats[category.title]) return 0

			// we have space for 5 collections across each row in the category;
			// if we have less than 10 (two rows of collections), we only need to protrude down one
			let opened_subcat = category.subcats.find(x=>x.title == this.opened_subcats[category.title])
			if (empty(opened_subcat)) return 0
			if (opened_subcat.collections.length <= 10) return 1
			// if we need 4 rows of collections, we need to protrude down two
			else if (opened_subcat.collections.length <= 15) return 2
			// if we need 5 rows of collections, we need to protrude down three
			else if (opened_subcat.collections.length <= 25) return 3
			// if we need more than 4 extra rows, the user will be able to scroll
			else return 4
		},

		set_position(category, column, row) {
			let top = (row + 1) * this.grid_margin + (row * this.tile_height)

			const opened_category = this.collection_categories.find(x => x.title == this.opened_category)
			if (opened_category && row > opened_category.row) {
				top += this.subject_external_systems_extra_height
			}

			category.top = top + "px"
			category.left = (column + 1) * this.grid_margin + (column * this.tile_width) + "px"
		},

		suppress_pointer_focus_open() {
			this.pointer_focus_suppressed = true
			clearTimeout(this._pointer_focus_timeout)
			this._pointer_focus_timeout = setTimeout(() => {
				this.pointer_focus_suppressed = false
			}, 150)
		},

		category_focused(category) {
			if (this.pointer_focus_suppressed) return
			if (this.opened_category == category.title) return
			this.opened_category = category.title
			this.reorganize_categories()
			this.set_location_path()
		},

		category_clicked(category) {
			if (this.opened_category == category.title) this.opened_category = null
			else this.opened_category = category.title
			this.reorganize_categories()
			this.set_location_path()
		},

		category_keydown(event, category) {
			if (event.key === 'Enter' || event.key === ' ') {
				event.preventDefault()
				this.category_clicked(category)
				return
			}

			if (event.key === 'ArrowRight') {
				event.preventDefault()
				this.focus_top_row_item_by_index(category, 0)
			} else if (event.key === 'ArrowLeft') {
				event.preventDefault()
				const top_row_nodes = this.top_row_nodes(category)
				this.focus_top_row_item_by_index(category, top_row_nodes.length - 1)
			}
		},

		top_row_nodes(category) {
			return Array.from(this.$el.querySelectorAll(
				`[data-top-row-item="true"][data-category-title="${CSS.escape(category.title)}"]`
			))
		},

		focus_top_row_item_by_index(category, index) {
			this.$nextTick(() => {
				const nodes = this.top_row_nodes(category)
				const target = nodes[index]
				if (target) target.focus()
			})
		},

		subcat_clicked(category, subcat) {
			let val = extobj(this.opened_subcats)
			if (val[category.title] == subcat.title) val[category.title] = null
			else val[category.title] = subcat.title
			this.opened_subcats = val	// make sure we update the value in lst
			this.reorganize_categories()
			this.set_location_path()
		},

		subcat_focused(category, subcat) {
			if (this.pointer_focus_suppressed) return
			if (this.opened_category != category.title) {
				this.opened_category = category.title
			}
			if (this.opened_subcats[category.title] == subcat.title) return
			let val = extobj(this.opened_subcats)
			val[category.title] = subcat.title
			this.opened_subcats = val
			this.reorganize_categories()
			this.set_location_path()
		},

		subcat_keydown(event, category, subcat) {
			event.stopPropagation()

			if (event.key === 'Enter' || event.key === ' ') {
				event.preventDefault()
				this.subcat_clicked(category, subcat)
				return
			}

			const subcat_index = category.subcats.findIndex(x => x.title == subcat.title)
			if (subcat_index < 0) return
			const top_row_nodes = this.top_row_nodes(category)
			const current_index = top_row_nodes.indexOf(event.currentTarget)
			if (current_index < 0) return

			if (event.key === 'ArrowRight') {
				event.preventDefault()
				this.focus_top_row_item_by_index(category, (current_index + 1) % top_row_nodes.length)
			} else if (event.key === 'ArrowLeft') {
				event.preventDefault()
				this.focus_top_row_item_by_index(category, current_index === 0 ? top_row_nodes.length - 1 : current_index - 1)
			} else if (event.key === 'ArrowDown') {
				event.preventDefault()
				this.focus_collection_by_index(category, subcat, 0)
			}
		},

		subject_button_keydown(event, category) {
			event.stopPropagation()

			if (event.key === 'Enter' || event.key === ' ') {
				event.preventDefault()
				event.currentTarget.click()
				return
			}

			const top_row_nodes = this.top_row_nodes(category)
			const current_index = top_row_nodes.indexOf(event.currentTarget)
			if (current_index < 0) return

			if (event.key === 'ArrowRight') {
				event.preventDefault()
				this.focus_top_row_item_by_index(category, (current_index + 1) % top_row_nodes.length)
			} else if (event.key === 'ArrowLeft') {
				event.preventDefault()
				this.focus_top_row_item_by_index(category, current_index === 0 ? top_row_nodes.length - 1 : current_index - 1)
			}
		},

		encode_path_section(s) {
			return s.replace(/ /g, '_').replace(/\//g, '-')	// .replace(/:/g, '@')
		},

		decode_path_section(s) {
			return s.replace(/_/g, ' ').replace(/-/g, '\/')
		},

		set_location_path() {
			// set the location path to mirror the opened category/subcategory
			let path = '/courses'
			if (this.opened_category) {
				path += '/' + this.encode_path_section(this.opened_category)
				if (this.opened_subcats[this.opened_category]) {
					path += '/' + this.encode_path_section(this.opened_subcats[this.opened_category])
				}
			}
			window.history.replaceState(null, '', path)
		},

		collection_clicked(category, subcat, collection) {
			if (this.opened_category != category.title) {
				this.category_clicked(category)
				return
			}

			this.$store.commit('set', ['last_lp_list', 'index'])
			this.$router.push({ path: collection.vue_route() })

			// in lti_chooser mode, emit event, which will show the collection
			if (this.lti_chooser) {
				this.$emit('go_to_collection', collection)
			}
		},

		visible_collection_nodes(category, subcat) {
			return Array.from(this.$el.querySelectorAll(
				`.k-courseindex2-subcat-collections-container[data-category-title="${CSS.escape(category.title)}"][data-subcat-title="${CSS.escape(subcat.title)}"] .k-courseindex2-collection[tabindex="-1"]`
			))
		},

		collection_keydown(event, category, subcat, collection_index) {
			event.stopPropagation()

			if (event.key === 'Enter' || event.key === ' ') {
				event.preventDefault()
				const collection = subcat.collections[collection_index]
				if (collection) this.collection_clicked(category, subcat, collection)
				return
			}

			if (event.key === 'ArrowRight') {
				event.preventDefault()
				this.focus_collection_by_index(category, subcat, (collection_index + 1) % subcat.collections.length)
			} else if (event.key === 'ArrowLeft') {
				event.preventDefault()
				this.focus_collection_by_index(category, subcat, collection_index === 0 ? subcat.collections.length - 1 : collection_index - 1)
			} else if (event.key === 'ArrowUp') {
				event.preventDefault()
				const subcat_index = category.subcats.findIndex(x => x.title == subcat.title)
				const top_row_nodes = this.top_row_nodes(category)
				const target = top_row_nodes.find(node => node.dataset.subcatTitle === subcat.title)
				if (target) target.focus()
				else this.focus_top_row_item_by_index(category, Math.max(0, subcat_index))
			}
		},

		focus_collection_by_index(category, subcat, index) {
			this.$nextTick(() => {
				const nodes = this.visible_collection_nodes(category, subcat)
				const target = nodes[index]
				if (target) target.focus()
			})
		},

		show_framework(category, collection) {
			let item_identifier = category.framework_identifier
			if (collection?.course_case_identifier) item_identifier = collection.course_case_identifier

			let data = { 
				framework_identifier: category.framework_identifier, 
				item_identifier: item_identifier, 
				no_framework_reset: true,
			}

			if (item_identifier != category.framework_identifier) {
				data.selected_items = [item_identifier]
				data.limit_to_selected_items = 'children'
			}

			vapp.$refs.satchel.execute('show').then(() => {
				vapp.$refs.satchel.execute('load_framework', data).then(() => {
				})
			})
		},

		show_community(category) {
			let s = {
				'Language Arts': '2021-summer-literacy-conference',
				'Mathematics': 'mathematics',
				'Science': 'science',
				'Social Studies': 'social-studies',
				'Physical Education': 'health',
				'Health': 'health',
				'Computer Science': 'computer-science',
				'World Languages': 'world-languages/',
				'English Language Development': 'ela-esol',
				'Fine Arts: Dance': 'dance',
				'Fine Arts: Dramatic Arts/Theatre': 'theatre-1409949552',
				'Fine Arts: Music': 'music-community',
				'Fine Arts: Visual Art': 'visual-art',
				// 'CTAE': '',
			}[category.subject]

			// if (empty(s)) {
			// 	this.$alert('A communities link has not been configured for this subject.')
			// 	return
			// }

			let url = 'https://community.gadoe.org'
			if (!empty(s)) url += `/groups/${s}/`

			window.open(url, 'cureum-community')
		},
		
		show_pd(category) {
			let url = `https://inspire.gadoe.org/src/sso_pdhub.php?session_id=${U.session_id}&subject=${category.subject}`
			window.open(url, 'pd_hub')
			// this.$alert('This Georgia Learns link will be functional soon.')
			return
		},

		open_pl_event_link(category) {
			let url = this.site_config.pl_event_links[category.subject]
			window.open(url, 'pl_events')
		},

		initiate_search() {
			// once we know standards-aligned assets are loaded, show search interface
			vapp.initiate_search({
				search_only: true,
				caller_component: this,
				dialog_title: 'Search Site Content',
				// home_collection: this.collection,
				default_source: 'site',
			})
		},
	}
}
</script>

<style lang="scss">
.k-courseindex2-wrapper {
	justify-content: center;
	.k-lp-grade-menu {
		max-width: 180px;
		margin-left: 4px;
	}
	.k-lp-subject-menu {
		max-width: 240px;
		margin-left: 4px;
		margin-right: 4px;
	}
	.k-lp-inactive {
		border-width: 2px;
		border-style: dotted;
		filter: brightness(0.9);
	}
	.k-lp-cmap-unspecified {
		opacity: 0.5;
	}
}
.k-courseindex2-control-wrapper {
	margin: 2px 4px;
}

///////////////////////////////////////////  ADDED FROM CUREUM
@media (max-width: 576px) {
	.k-class-tile {
		width: 160px!important;
		height: 90px!important;

		img {
			width: 22px;
		}

		.k-class-edit-btn {
			right: 10px;
			bottom: 4px;
		}
	}

	.k-collection-tile {
		width: 100px!important;
		height: 100px!important;
		border-radius: 50px!important;
		font-size: 12px!important;
		line-height: 15px!important;
	}
}

///////////////////////////////////////////////////
.k-courseindex2-collections-container-header {
	font-size: 18px;
	padding-top: 12px;
	margin-bottom: -8px;
}

.k-courseindex2-collections-container {	// xx mainContainer
	position: relative;
	height: 1240px;		// set by style
	padding: 0;
}

.k-courseindex2-category-wrapper {	// xx subjectWrapper
	text-align: left;
	position: absolute;
	transition: left .25s, top .25s;
	cursor: pointer;
	outline: none;
}

.k-courseindex2-category-container {
	position: relative;
	overflow: auto;
	padding: 16px;	// set by style
	margin: 0;
	width: 200px;	// set by style
	height: 200px;	// set by style
	border-radius: 25px;	// set by style
	background-color: #333;
	color: white;
	box-shadow: 0 0 15px 1px rgba(0, 0, 0, .25);
	transition: width .25s, height .25s, margin .25s;
}

.k-courseindex2-category-wrapper:focus-visible .k-courseindex2-category-container {
	outline: 3px solid #ffffff;
	outline-offset: 2px;
	box-shadow: 0 0 0 2px #0d47a1, 0 0 0 5px #ffffff, 0 0 0 7px #0d47a1;
}

.k-courseindex2-subcat:focus-visible,
.k-courseindex2-collection:focus-visible {
	outline: 3px solid #ffffff;
	outline-offset: 2px;
	box-shadow: 0 0 0 4px rgba(255, 255, 255, 0.35);
	background-color: rgba(0, 0, 0, 0.92) !important;
	border-color: #ffffff !important;
	color: #ffffff !important;
}

.k-courseindex2-subcat:focus-visible *,
.k-courseindex2-collection:focus-visible * {
	color: #ffffff !important;
}

.k-courseindex2-collection:focus-visible .k-courseindex2-collection-icons {
	background-color: rgba(255, 255, 255, 0.92);
}

.k-courseindex2-collection:focus-visible .k-courseindex2-collection-icons .v-icon {
	color: #000000 !important;
}

.k-courseindex2-category-title {
	font-size: 24px;	// set by style
	line-height: 1.2em;
	font-weight: bold;
}

.k-courseindex2-category-icon {
	position: absolute;
	top: calc(100% - 92px);	// set by style
	left: calc(100% - 92px);	// set by style
	width: 80px;	// set by style
	height: 80px;	// set by style
	border-radius: 12px;	// set by style
	background-color: #fff;
	opacity: 0.85;
	transition: all .25s;
	img {
		width: 100%; 
		height: 100%;
		padding: 3%;
		border-radius: 20%;
	}
}

.k-courseindex2-subject-external-systems-btns {
	display: none;
	position: absolute;
	top: 24px;	// set by style
	left: 12px;
	right: 12px;
	flex-wrap: wrap;
	row-gap: 4px;
}

.k-courseindex2-subject-es-btn {
	border-radius: 10px;
	background-color: rgba(0, 0, 0, .4);
	font-size: 14px;
	padding: 4px 12px;
	margin: 0 4px;
	transition: all .25s;
	outline: none;
	white-space: nowrap;
}

.k-courseindex2-subject-es-btn:hover {
	background-color: rgba(0, 0, 0, .6);
}

.k-courseindex2-subject-es-btn:focus-visible {
	outline: 3px solid #ffffff;
	outline-offset: 2px;
	box-shadow: 0 0 0 4px rgba(255, 255, 255, 0.35);
	background-color: rgba(0, 0, 0, 0.92) !important;
}

.k-courseindex2-category-open {
	width: 688px;	// set by style

	.k-courseindex2-subcats-container, .k-courseindex2-subcat {
		opacity: 1;
	}
	.k-courseindex2-category-title {
		width: 312px;	// set by style
		padding-left: 44px;	// set by style
	}
	.k-courseindex2-category-icon {
		left: 12px;	// set by style
		top: 18px;	// set by style
		width: 40px;	// set by style
		height: 40px;	// set by style
		border-radius: 6px;	// set by style
	}
	.k-courseindex2-subject-external-systems-btns {
		display: flex;
	}
}
.k-courseindex2-category-subcategory-selected {
	height: 444px;	// set by style

	.k-courseindex2-subject-external-systems-btns {
		top: 100px;	// set by style
		margin-left: -4px;
	}
}

.k-courseindex2-subcats-container {
	position: absolute;
	right: 12px;	// set by style
	top: 12px;	// set by style
	border-radius: 16px;	// set by style
	background-color: rgba(0, 0, 0, .1);
	opacity: 0.0;
	overflow: hidden;
	transition: opacity .25s;
	display: flex;
}

.k-courseindex2-subcat {
	width: 92px;	// set by style
	height: 92px;	// set by style
	border-radius: 10px;	// set by style
	margin: 6px;	// set by style
	padding: 8px;	// set by style
	font-weight: bold;
	font-size: 12px;	// set by style
	line-height: 1.2em;
	background-color: rgba(0, 0, 0, .4);
	opacity: 0.0;
	transition: background-color .25s, opacity .75s;
	border:1px solid #666;
	outline: none;
}

.k-courseindex2-subcat:hover {
	background-color: rgba(0, 0, 0, .6);
}

.k-courseindex2-subcat-selected {
	background-color: rgba(0, 0, 0, .8);
	border:1px solid #fff;
}

.k-courseindex2-subcat-collections-container {
	position: absolute;
	top: 130px;	// set by style
	left: 4px;
	max-height: calc(100% - 150px);
	max-width: calc(100% - 40px);
	background-color: transparent;
	overflow: hidden;
	display: grid;
	grid-template-columns: repeat(5, 1fr);
	grid-gap: 0;
}

.k-courseindex2-subcat-collections-container-selected {
	overflow: visible;

	.k-courseindex2-subcat-collections-container {
		opacity: 1;
	}

	.k-courseindex2-collection {
		opacity: 1;
	}
}

.k-courseindex2-collection {
	position: relative;
	width: 120px;	// set by style
	height: 120px;	// set by style
	border-radius: 10px;	// set by style
	margin: 8px;	// set by style
	padding: 8px;	// set by style
	background-color: #222;
	font-size: 15px;	// set by style
	line-height: 1.2em;
	border: 2px solid rgba(255,255,255,0.4);
	opacity: 0;
	transition: background-color .25s, opacity .75s;
	outline: none;
}
.k-courseindex2-collection:hover {
	.k-courseindex2-collection-icons {
		opacity: 0.8;
	}
}

.k-courseindex2-collection-icons {
	position: absolute;
	right: 8px;
	bottom: 8px;
	background-color: rgba(255,255,255,1);
	border-radius: 8px;
	padding: 3px 6px 3px 6px;
	opacity: 0.5
}

.k-courseindex2-collection-code {
	position: absolute;
	left: 4px;
	bottom: 0;
	color: #fff;
	opacity: 1;
	font-size: 10px;
	display: none;
}

.k-courseindex2-collection-code-visible {
	.k-courseindex2-collection-code { display: block; }
}

.k-courseindex2-shadow-text {
	text-shadow: 0.5px 0.5px 3px #000;
	font-weight: bold;
}

.k-courseindex2-bold-text {
	font-weight: bold;
}

// this will also be used for other tabs...
.k-custom-tab-content-description {
	background-color: #fff;
	padding: 16px;
	font-size: 20px;
	line-height: 28px;
	border-radius: 8px;
	max-width: 800px;
	margin: 0 auto 12px auto;
}
</style>
